<script setup lang="ts">
import { computed, onBeforeMount, reactive, ref, inject, Ref } from 'vue';
import { getCurrentLocale } from '@/i18n';
import cloneDeep from 'lodash/cloneDeep';
import { useI18n } from 'vue-i18n';
import { TOTAL_COST, UNASSIGNED } from '@/constants';
import { useWorksheetComposable } from './useWorksheetComposable';
import { useComponentComposable } from '@/components/useComponentComposable';
import { useHotelManagementStore } from '@/store/HotelManagementStore';
import { useAdminQuoteStore } from '@/store/AdminQuoteStore';
import { useSubmitWorksheetStore } from '@/store/SubmitWorksheetStore';
import { usePackageManagementStore } from '@/store/PackageManagementStore';
import { formatDate } from '@/utils/dateUtils';
import { getTextVariant } from '@/utils/stringUtils';
import { useDialog } from 'primevue/usedialog';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import DataTable, { DataTableCellEditCancelEvent } from 'primevue/datatable';
import DatePicker from 'primevue/datepicker';
import Dialog from 'primevue/dialog';
import InputNumber from 'primevue/inputnumber';
import InputText from 'primevue/inputtext';
import TableColumn from 'primevue/column';
import Tab from 'primevue/tab';
import TabList from 'primevue/tablist';
import TabPanel from 'primevue/tabpanel';
import TabPanels from 'primevue/tabpanels';
import Tabs from 'primevue/tabs';
import HotelInform from '@/components/modal/worksheet/HotelInform.vue';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import LinkedQuotesEditor from '@/components/modal/worksheet/LinkedQuotesEditor.vue';
import QuoteAdjustmentToolModal from '@/components/modal/worksheet/QuoteAdjustmentToolModal.vue';
import RunOfHouseSelection from './RunOfHouseSelection.vue';
import AdjustOptionMarginModal from './AdjustOptionMarginModal.vue';
import HTMLEditor from '@/components/common/HTMLEditor.vue';
import ViewPricingModal from './ViewPricingModal.vue';
import ViewPDFFile from '@/components/modal/utils/ViewPDFFile.vue';

const dialogRef = inject('dialogRef') as Ref;
const reloadList = dialogRef.value.options.emits.reloadList;

const {
  filterPriceList,
  PRICE_KEY,
  titleHotel,
  authorizations,
  canViewPromotion,
  canEditPromotion,
  canViewAgencyComment,
  canEditAgencyComment,
  hasUserPermission,
  loadingIcon,
  formatDataBeforeSaveRow,
  semistarImage,
  handleRedirectLink,
  canViewTermConditions,
  canEditTermConditions,
  canAdjustOptionMargin,
  handleConvertBrTag,
  checkEditChildInput,
  handleChildInput
} = useWorksheetComposable();

const { fallBackImage } = useComponentComposable();

const { t } = useI18n({ useScope: 'global' });
const dialog = useDialog();
const toast = useToast();
const currentLanguage = getCurrentLocale();
const adminQuoteStore = useAdminQuoteStore();
const hotelManagementStore = useHotelManagementStore();
const submitWorksheetStore = useSubmitWorksheetStore();
const packageStore = usePackageManagementStore();

const disablePreviewPDF = ref(true);
const editingRows = ref<any>({});
const roomAdjustmentInfo = ref([]);
const numberDynamicColumns = ref<any>([]);
const isLoadingTool = ref<any>(false);
const worksheetHotelPrices = ref<any>([]);

const oldChildValue = ref([]);
const canEditHeader = authorizations?.quote_details_header_edit?.quote_details_header_edit;

const visible = ref(false);
const isPromotionModal = ref(false);
const newPrice = ref(false);
const newHcms = ref(false);
const isEmptyRespond = ref(false);
const errorMessage = ref('');
const isLoading = ref(true);
const currentOption = ref(0);
const doublePricePublic = ref<any>([]);
const loadingWorksheetHotel = ref(false);
const canEditRoh = authorizations?.worksheet_hotel?.roh_edit;
const isShowError = ref(false);
const validationInputs = reactive<Record<string, boolean>>({});

const priceDetail = ref({
  currency: '',
  rateOfExchange: 1
});

const promoEditedData = ref({
  index: null,
  data: ''
});

const editComment = ref({
  index: null,
  data: ''
});

const editIntro = ref({
  isEdit: false,
  data: ''
});

const editTermConditions = ref({
  isEdit: false,
  data: ''
});

const editAdditionalHotel = ref({
  index: '',
  data: ''
});

const formatHotelData = reactive({
  depDate: '',
  duration: ''
});

const editExpiryDate = ref({
  isEdit: false,
  data: null
});

const editDateBooked = ref({
  isEdit: false,
  data: null
});

const editAdult = ref({
  isEdit: false,
  data: ''
});

const editChild = ref({
  isEdit: false,
  data: ''
});

const editNumberOfRoom = ref({
  isEdit: false,
  data: ''
});

const editHotelRoomCategory = ref({
  index: '',
  data: ''
});

const hotelWorkSheet = computed(() => {
  return adminQuoteStore.currentQuote;
});

const isIncentiveGroupType = computed(() => {
  return hotelWorkSheet.value.gType.includes('Incentive');
});

const handleSaveAdult = (adt, hotel) => {
  hotel.adt = adt;
  editAdult.value.isEdit = false;
};

const handleSaveChild = (chd, hotel) => {
  hotel.chd = chd;
  editChild.value.isEdit = false;
};

const handleSaveNumberOfRoom = (numOfRooms, hotel) => {
  hotel.numOfRooms = numOfRooms;
  editNumberOfRoom.value.isEdit = false;
};

const changeLinkedQuoteHotel = (event: any) => {
  hotelWorkSheet.value.linkedQuotes = event;
};

const convertOccNameTable = (rowName: string) => {
  return titleHotel.find((th) => th.key === rowName)?.name;
};

const getMarAdjAndTotalMar = (changeData, key, clonePrice) => {
  const tempMarAdj = Array(changeData[key].length);
  const tempTotalMar = Array(changeData[key].length);
  for (let i = 0; i < changeData[key].length; i++) {
    tempMarAdj[i] = (Number(clonePrice.marAdj[key][i]) + changeData[key][i]).toFixed(2);
    tempTotalMar[i] = (Number(clonePrice.totalMar[key][i]) + changeData[key][i]).toFixed(2);
  }
  return { tempMarAdj: tempMarAdj, tempTotalMar: tempTotalMar };
};

const getSellingPriceAndTotalMar = (changeData, key, clonePrice) => {
  const tempSellingPrice = Array(changeData[key].length);
  const tempTotalMar = Array(changeData[key].length);
  for (let i = 0; i < changeData[key].length; i++) {
    tempSellingPrice[i] = (Number(clonePrice.sellingPrice[key][i]) + changeData[key][i]).toFixed(2);
    tempTotalMar[i] = (Number(clonePrice.totalMar[key][i]) + changeData[key][i]).toFixed(2);
  }
  return { tempSellingPrice: tempSellingPrice, tempTotalMar: tempTotalMar };
};

const conditionSellingPrice = (changeData, clonePrice, key) => {
  let tempMarAdj;
  let totalMar;
  if (key === 'childPrices') {
    const temp = getMarAdjAndTotalMar(changeData, key, clonePrice);
    tempMarAdj = temp.tempMarAdj;
    totalMar = temp.tempTotalMar;
  } else {
    tempMarAdj = (Number(clonePrice.marAdj[key]) + changeData[key]).toFixed(2);
    totalMar = (Number(clonePrice.totalMar[key]) + changeData[key]).toFixed(2);
  }
  return { tempMarAdj: tempMarAdj, totalMar: totalMar };
};

const conditionMarginAdjustment = (changeData, clonePrice, key) => {
  let tempSellingPrice;
  let tempTotalMar;
  if (key === 'childPrices') {
    const temp = getSellingPriceAndTotalMar(changeData, key, clonePrice);
    tempSellingPrice = temp.tempSellingPrice;
    tempTotalMar = temp.tempTotalMar;
  } else {
    tempSellingPrice = (Number(clonePrice.sellingPrice[key]) + changeData[key]).toFixed(2);
    tempTotalMar = (Number(clonePrice.totalMar[key]) + changeData[key]).toFixed(2);
  }
  return { tempSellingPrice: tempSellingPrice, tempTotalMar: tempTotalMar };
};

const onRowEditSave = ({ newData, data }: any, hotelData: any, indexOption: number, indexHotel: number, roomIndex: number) => {
  const { rowCanAutoCalulations, editedRow, changeData, hasChangeData } = formatDataBeforeSaveRow(newData, data, oldChildValue.value);
  if (!hasChangeData) {
    return;
  }

  const clonePrice = cloneDeep(hotelData.hotels[indexOption].hotels[indexHotel].rooms[roomIndex].price);

  switch (editedRow) {
    case rowCanAutoCalulations.sellingPrice:
      for (const key in changeData) {
        const { tempMarAdj, totalMar } = conditionSellingPrice(changeData, clonePrice, key);
        hotelData.hotels[indexOption].hotels[indexHotel].rooms[roomIndex].price.marAdj[key] = tempMarAdj;
        hotelData.hotels[indexOption].hotels[indexHotel].rooms[roomIndex].price.totalMar[key] = totalMar;
      }
      break;
    case rowCanAutoCalulations.marginAdjustment:
      for (const key in changeData) {
        const { tempSellingPrice, tempTotalMar } = conditionMarginAdjustment(changeData, clonePrice, key);
        hotelData.hotels[indexOption].hotels[indexHotel].rooms[roomIndex].price.sellingPrice[key] = tempSellingPrice;
        hotelData.hotels[indexOption].hotels[indexHotel].rooms[roomIndex].price.totalMar[key] = tempTotalMar;
      }
      break;
    case rowCanAutoCalulations.costAdjustment:
      for (const key in changeData) {
        hotelData.hotels[indexOption].hotels[indexHotel].rooms[roomIndex].price.hotelCostAdj = newData;

        const { tempSellingPrice } = conditionMarginAdjustment(changeData, clonePrice, key);
        hotelData.hotels[indexOption].hotels[indexHotel].rooms[roomIndex].price.sellingPrice[key] = tempSellingPrice;
      }
      break;
  }

  hotelData.hotels[indexOption].hotels[indexHotel].rooms[roomIndex].price[newData.rowName] = formatChildPrices(newData);
  worksheetHotelPrices.value[renderStartIndex(hotelData.hotels, indexOption, indexHotel) + roomIndex] = filterPriceList(hotelData.hotels[indexOption].hotels[indexHotel].rooms[roomIndex].price, authorizations?.worksheet_hotel);
  handleFormatData();
};

const formatChildPrices = (hotelData: any) => {
  if (hotelData.childPrices && Array.isArray(hotelData.childPrices)) {
    hotelData.childPrices = hotelData.childPrices.map((price) => {
      if (typeof price === 'number') {
        return price.toFixed(2);
      }
      return price;
    });
  }
  return hotelData;
};

const replaceNullsWithZero = (data: string[]) => {
  if (data) {
    return data.map((price) => (price === null ? '0.00' : price));
  }
  return null;
};

const renderDataRoom = (data) => {
  const hotelRooms = data?.map((room) => {
    for (const property in room.price) {
      delete room.price[property]?.key;
      delete room.price[property]?.rowName;
      if (replaceNullsWithZero(room.price[property].childPrices)) {
        room.price[property].childPrices = replaceNullsWithZero(room.price[property].childPrices);
      }
    }
    return {
      price: room.price,
      code: room.code,
      checkinDate: room.checkinDate,
      checkoutDate: room.checkoutDate,
      hotelDuration: room.hotelDuration,
      promotion: room.promotion,
      comment: room.comment,
      room: room.room,
      roomFr: room.roomFr,
      longName: room.longName,
      longNameFr: room.longNameFr,
      totalTax: room.totalTax,
      maxPaxAdult: room.maxPaxAdult,
      maxPaxChild: room.maxPaxChild,
      maxPaxRoom: room.maxPaxRoom,
      minAdPerChild: room.minAdPerChild,
      minPaxAdult: room.minPaxAdult,
      minPaxChild: room.minPaxChild,
      minPaxRoom: room.minPaxRoom,
      mandatoryInfo: room.mandatoryInfo,
      rateOfExchange: room.rateOfExchange,
      currency: room.currency,
      roomInfo: room.roomInfo,
      detailedOccupancy: room.detailedOccupancy,
      adultTransferCost: room.adultTransferCost,
      childTransferCost: room.childTransferCost
    };
  });

  return hotelRooms;
};

const getCurrencyAndRateExchange = () => {
  if (newDataPricing.roomData.length) {
    priceDetail.value.currency = newDataPricing.roomData[0].currency;
    priceDetail.value.rateOfExchange = newDataPricing.roomData[0].rateOfExchange;
  }
};

const handleRenderHotel = (hotel: any) => {
  return {
    comment: hotel.comment,
    hCode: hotel.hCode,
    promotion: hotel.promotion,
    additionalHotelDetail: hotel.additionalHotelDetail,
    hotel: hotel.hotel,
    code: hotel?.code,
    hNote: hotel.hNote,
    adminMaxRoom: hotel?.adminMaxRoom,
    hotelChain: hotel.hotelChain,
    rooms: renderDataRoom(hotel.rooms)
  };
};

const handleRenderOption = (hotelWorkSheetData: any) => {
  const optionTemp: any = [];
  hotelWorkSheetData.hotels?.forEach((option: any, indexOption: number) => {
    const hotelPreferTemp: any = [];
    const hotelTemp: any = [];
    option.hotelPrefer?.forEach((hotel: any) => {
      hotelPreferTemp.push(handleRenderHotel(hotel));
    });

    option.hotels?.forEach((hotel: any) => {
      hotelTemp.push(handleRenderHotel(hotel));
    });
    const hotelCurrency = priceDetail.value.currency ? priceDetail.value.currency : option.currency;
    const hotelRateOfExchange = priceDetail.value.rateOfExchange ? priceDetail.value.rateOfExchange : option.rateOfExchange;
    const hotelForm = {
      currency: hotelCurrency,
      rateOfExchange: hotelRateOfExchange,
      adt: option.adt,
      chd: option.chd,
      comment: option.comment,
      depDate: option.depDate,
      retDate: option.retDate,
      dest: option.dest,
      destCode: option.destCode,
      duration: option.duration,
      hNote: option.hNote,
      numOfRooms: option.numOfRooms,
      promotion: option.promotion,
      hotelPrefer: hotelPreferTemp,
      hotels: hotelTemp,
      airportCode: option.airportCode,
      additionalNote: option.additionalNote,
      brochure: option.brochure,
      netMargin: doublePricePublic.value[indexOption]
    };
    optionTemp.push(hotelForm);
  });

  return optionTemp;
};

const onSave = async (hotelWorkSheetData: any) => {
  const optionTemp = handleRenderOption(hotelWorkSheetData);
  const payload = {
    qType: hotelWorkSheetData.qType,
    bookedDate: new Date(formatDate(hotelWorkSheet.value?.bookedDate, undefined, false)),
    expires: new Date(formatDate(hotelWorkSheet.value?.expires, undefined, false)),
    hotels: optionTemp,
    intro: handleConvertBrTag(hotelWorkSheet.value?.intro),
    termConditions: handleConvertBrTag(hotelWorkSheet.value?.termConditions),
    linkedQuotes: hotelWorkSheet.value?.linkedQuotes
  };
  submitWorksheetStore
    .handleUpdateWorkSheet(payload, hotelWorkSheetData.sk)
    .then(() => {
      disablePreviewPDF.value = false;
      toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
      reloadList();
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
    });
};

const handleSavePromo = (data) => {
  data.promotion = promoEditedData.value.data;
  promoEditedData.value = {
    index: null,
    data: ''
  };
};

const handleSaveComment = (data) => {
  data.comment = editComment.value.data;
  editComment.value = {
    index: null,
    data: ''
  };
};

const handleSaveIntro = (hotelData) => {
  hotelData.intro = editIntro.value.data;
  editIntro.value = {
    isEdit: false,
    data: ''
  };
};

const handleSaveTermCondition = (hotelData) => {
  hotelData.termConditions = editTermConditions.value.data;
  editTermConditions.value = {
    isEdit: false,
    data: ''
  };
};

const handleSaveAdditionalHotel = (data) => {
  data.additionalHotelDetail = editAdditionalHotel.value.data;
  editAdditionalHotel.value = {
    index: null,
    data: ''
  };
};

const newDataPricing = reactive({
  indexOption: 0,
  hotelType: '',
  hotelData: '',
  roomData: '',
  promotions: '',
  indexHotel: 0,
  additionalHotelDetail: ''
});

const getNewPricing = async (body: any, worksheetData: any, indexOption: number, indexHotel: number, hotelType: string) => {
  isLoading.value = true;
  newDataPricing.indexOption = indexOption;
  newDataPricing.hotelType = hotelType;
  newDataPricing.indexHotel = indexHotel;
  hotelManagementStore
    .getPricingQuote(worksheetData.sk, body)
    .then((res) => {
      newPrice.value = true;
      const cloneHotel = cloneDeep(worksheetData?.hotels[indexOption][hotelType][indexHotel]);
      newDataPricing.roomData = res.data?.hotelPricing;
      newDataPricing.hotelData = cloneHotel;
    })
    .catch((error) => {
      if (error.response?.status === 404) {
        errorMessage.value = error.response?.data.detail;
      }
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const getNewPromotion = async (body: any, worksheetData: any, indexOption: number, indexHotel: number, hotelType: string) => {
  isLoading.value = true;
  newDataPricing.indexOption = indexOption;
  newDataPricing.hotelType = hotelType;
  newDataPricing.indexHotel = indexHotel;
  hotelManagementStore
    .getHcmsPromotion(worksheetData.sk, body)
    .then((res) => {
      if (res) {
        newHcms.value = true;
        isEmptyRespond.value = !res.data;
        newDataPricing.promotions = res.data?.promotion;
        newDataPricing.additionalHotelDetail = res.data?.additionalHotelDetail;
      }
    })
    .catch(() => {
      newHcms.value = false;
      newDataPricing.promotions = '';
      newDataPricing.additionalHotelDetail = '';
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const handleCloseAndApplyPromotion = async (worksheetData: any, newDataPricing: any) => {
  worksheetData.hotels[newDataPricing.indexOption][newDataPricing.hotelType][newDataPricing.indexHotel].promotion = newDataPricing.promotions;
  worksheetData.hotels[newDataPricing.indexOption][newDataPricing.hotelType][newDataPricing.indexHotel].additionalHotelDetail = newDataPricing.additionalHotelDetail;
  visible.value = false;
  onSave(hotelWorkSheet.value);
};

const formatPrice = (worksheetData: any) => {
  worksheetHotelPrices.value = [];
  numberDynamicColumns.value = [];

  worksheetData.hotels.forEach((option: any) => {
    option.hotels.forEach((hotel: any) => {
      hotel.rooms.forEach((room: any) => {
        const listPrice = filterPriceList(room.price, authorizations?.worksheet_hotel);
        worksheetHotelPrices.value.push(listPrice);
        numberDynamicColumns.value.push(listPrice?.[0]?.childPrices?.length);
      });
    });
  });
};

const onSaveAll = (hotel, worksheetData) => {
  worksheetData.hotels[newDataPricing.indexOption].hotels[newDataPricing.indexHotel].rooms = hotel.rooms;
  formatPrice(worksheetData);
  onSave(hotelWorkSheet.value);
  handleFormatData();
  visible.value = false;
};

const previewNewPriceHotel = (newData, worksheetData) => {
  priceDetail.value.currency = '';
  priceDetail.value.rateOfExchange = 1;
  const preRoomValue = worksheetData?.hotels[newData.indexOption]?.hotels[newData.indexHotel]?.rooms;
  const dialogRef = dialog.open(ViewPricingModal, {
    props: {
      header: t('common.preview'),
      maximizable: true,
      style: {
        width: '90vw'
      },
      modal: true
    },
    data: {
      hotelData: newData.hotelData,
      roomData: newData.roomData,
      oldRoomsValue: preRoomValue,
      permissions: authorizations?.worksheet_hotel
    },
    emits: {
      onSaveSelectRoom: (hotel) => {
        getCurrencyAndRateExchange();
        onSaveAll(hotel, worksheetData);
        removeCostCalculationData();
        dialogRef.close();
      }
    }
  });
};

const checkExistData = (cloneData: any, field: string, key: string, index: number) => {
  return cloneData && cloneData[field] && cloneData[field][key] && cloneData[field][key][index];
};

const getMarAdjAndSellingPriceAndTotalMar = (valueChange: any, key: string, cloneData: any, changeData: any) => {
  const tempMarAdj = Array(valueChange[key].length).fill(0);
  const tempSellingPrice = Array(valueChange[key].length).fill(0);
  const tempTotalMar = Array(valueChange[key].length).fill(0);
  for (let i = 0; i < valueChange[key].length; i++) {
    if (checkExistData(cloneData, 'sellingPrice', key, i) && changeData) {
      tempMarAdj[i] = Number(cloneData.sellingPrice[key][i]) ? Number(cloneData.marAdj[key][i]) + changeData[key][i] : Number(cloneData.marAdj[key][i]);
      tempTotalMar[i] = Number(cloneData.sellingPrice[key][i]) ? Number(cloneData.totalMar[key][i]) + changeData[key][i] : Number(cloneData.totalMar[key][i]);
      tempSellingPrice[i] = Number(cloneData.sellingPrice[key][i]) ? Number(cloneData.sellingPrice[key][i]) + changeData[key][i] : Number(cloneData.sellingPrice[key][i]);
    }
  }
  return { tempMarAdj: tempMarAdj, tempSellingPrice: tempSellingPrice, tempTotalMar: tempTotalMar };
};

const onSaveAdjustOptionMargin = (hotelDetail: any, newData: any, indexOption: number, indexHotel: number) => {
  const valueChange: any = {
    doublePrice: newData.adultMargin,
    quadPrice: newData.adultMargin,
    singlePrice: newData.adultMargin,
    triplePrice: newData.adultMargin,
    fiveInRoomPrice: newData.adultMargin,
    sixInRoomPrice: newData.adultMargin
  };

  newData.hotelRoomCate.forEach((roomCatIndex: number) => {
    valueChange.childPrices = Array(numberDynamicColumns.value[renderStartIndex(hotelWorkSheet.value.hotels, indexOption, indexHotel) + roomCatIndex]).fill(newData.childMargin);
    const cloneData: any = cloneDeep(hotelDetail.rooms[roomCatIndex].price);
    const { changeData } = formatDataBeforeSaveRow(valueChange, cloneData.marAdj);
    for (const key in valueChange) {
      if (key === 'childPrices') {
        const temp = getMarAdjAndSellingPriceAndTotalMar(valueChange, key, cloneData, changeData);
        hotelDetail.rooms[roomCatIndex].price.marAdj[key] = temp.tempMarAdj;
        hotelDetail.rooms[roomCatIndex].price.sellingPrice[key] = temp.tempSellingPrice;
        hotelDetail.rooms[roomCatIndex].price.totalMar[key] = temp.tempTotalMar;
      } else {
        hotelDetail.rooms[roomCatIndex].price.marAdj[key] = Number(cloneData.sellingPrice[key]) ? Number(cloneData.marAdj[key]) + changeData[key] : Number(cloneData.marAdj[key]);
        hotelDetail.rooms[roomCatIndex].price.sellingPrice[key] = Number(cloneData.sellingPrice[key]) ? Number(cloneData.sellingPrice[key]) + changeData[key] : Number(cloneData.sellingPrice[key]);
        hotelDetail.rooms[roomCatIndex].price.totalMar[key] = Number(cloneData.sellingPrice[key]) ? Number(cloneData.totalMar[key]) + changeData[key] : Number(cloneData.totalMar[key]);
      }
    }

    worksheetHotelPrices.value[renderStartIndex(hotelWorkSheet.value.hotels, indexOption, indexHotel) + roomCatIndex] = filterPriceList(hotelDetail.rooms[roomCatIndex].price, authorizations?.worksheet_hotel);
  });
};

const handleOpenAdjustModal = (hotel: any, indexOption: number, indexHotel: number) => {
  dialog.open(AdjustOptionMarginModal, {
    data: {
      roomData: hotel.rooms
    },
    props: {
      header: t('worksheet.adjust_option_margin'),
      maximizable: true,
      style: {
        width: '30vw'
      },
      breakpoints: {
        '960px': '75vw',
        '640px': '90vw'
      },
      modal: true
    },
    emits: {
      onApply: (data: any) => {
        onSaveAdjustOptionMargin(hotel, data, indexOption, indexHotel);
      }
    }
  });
};

const removeCostCalculationData = () => {
  const worksheetID = hotelWorkSheet.value.sk;
  if (hotelWorkSheet.value?.hotels?.length && currentOption.value <= hotelWorkSheet.value?.hotels?.length - 1) {
    packageStore.deleteQuoteAdjusmentInfo(worksheetID, currentOption.value).catch((error) => {
      toast.add({ severity: 'error', summary: '', detail: error.response?.data?.message, life: 5000 });
    });
  }
};

const onSaveCostAdjustmentTool = (hotelData: any, data: any, indexOption: number, indexHotel: number) => {
  const worksheetID = hotelWorkSheet.value.sk;
  const optionNo = indexHotel;
  packageStore
    .saveQuoteAdjusmentInfo(worksheetID, optionNo, roomAdjustmentInfo.value)
    .then(() => {
      toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
      data?.forEach((room, roomIndex) => {
        const cloneData: any = cloneDeep(hotelData.rooms[roomIndex].price);
        const { changeData } = formatDataBeforeSaveRow(room, cloneData.costAdj);

        for (const key in changeData) {
          hotelData.rooms[roomIndex].price.hotelCostAdj = room;
          hotelData.rooms[roomIndex].price.costAdj = room;

          const { tempSellingPrice } = conditionMarginAdjustment(changeData, cloneData, key);
          hotelData.rooms[roomIndex].price.sellingPrice[key] = tempSellingPrice;
        }

        worksheetHotelPrices.value[renderStartIndex(hotelWorkSheet.value.hotels, indexOption, indexHotel) + roomIndex] = filterPriceList(hotelData.rooms[roomIndex].price, authorizations?.worksheet_hotel);
        handleFormatData();
      });
    })
    .catch((error) => {
      toast.add({ severity: 'error', summary: '', detail: error, life: 5000 });
    });
};

const handleOpenCostCalculationModal = (hotelWorkSheetOption: any, indexOption: number, indexHotel: number) => {
  const worksheetID = hotelWorkSheet.value.sk;
  const optionNo = indexHotel;
  isLoadingTool.value = true;
  packageStore
    .getQuoteAdjusmentInfo(worksheetID, optionNo)
    .then((res) => {
      roomAdjustmentInfo.value = res.data;
      dialog.open(QuoteAdjustmentToolModal, {
        data: {
          roomData: roomAdjustmentInfo.value
        },
        props: {
          header: t('worksheet.quote_calculation_tool') + ' (' + worksheetID + ')',
          style: { width: '90vw' },
          maximizable: true,
          modal: true
        },
        emits: {
          onApply: (data) => {
            onSaveCostAdjustmentTool(hotelWorkSheetOption, data, indexOption, indexHotel);
          }
        }
      });
    })
    .catch((error) => {
      toast.add({ severity: 'error', summary: '', detail: error.response?.data?.message, life: 5000 });
    })
    .finally(() => {
      isLoadingTool.value = false;
    });
};

const previewNewPromotionHotel = (newData) => {
  dialog.open(ViewPricingModal, {
    props: {
      header: t('common.preview'),
      maximizable: true,
      style: {
        width: '95vw'
      },
      breakpoints: {
        '960px': '75vw',
        '640px': '90vw'
      },
      modal: true
    },
    data: {
      promotions: newData.promotions,
      additionalHotelDetail: newData.additionalHotelDetail,
      permissions: authorizations?.worksheet_hotel
    }
  });
};

const handleOpenModal = async (hotelWorkSheetData: any, indexOption: number, indexHotel: number, hotelType: string, isPromotion?: boolean) => {
  const roomCodes: string[] = [];
  hotelWorkSheetData.hotels[indexOption]?.[hotelType][indexHotel].rooms.forEach((item: any) => {
    roomCodes.push(item.code);
  });
  const body = {
    depDate: hotelWorkSheetData.hotels[indexOption]?.depDate,
    retDate: hotelWorkSheetData.hotels[indexOption]?.retDate,
    gateCode: null,
    airportCode: hotelWorkSheetData.hotels[indexOption]?.airportCode,
    hCode: hotelWorkSheetData.hotels[indexOption]?.[hotelType][indexHotel].hCode,
    numOfRooms: hotelWorkSheetData.hotels[indexOption]?.numOfRooms,
    adtNo: hotelWorkSheetData.hotels[indexOption]?.adt,
    chdNo: hotelWorkSheetData.hotels[indexOption]?.chd,
    brochure: hotelWorkSheetData.hotels[indexOption]?.brochure,
    option: `${+indexOption + 1}`,
    lang: hotelWorkSheetData.lang,
    roomCode: roomCodes.join('|')
  };

  if (isPromotion) {
    getNewPromotion(body, hotelWorkSheetData, indexOption, indexHotel, hotelType);
    isPromotionModal.value = true;
  } else {
    getNewPricing(body, hotelWorkSheetData, indexOption, indexHotel, hotelType);
    isPromotionModal.value = false;
  }
  visible.value = true;
};

const getHotelRoom = (hotel) => {
  let result = '';
  hotel.rooms?.forEach((room, indexR) => {
    if (indexR > 0) {
      result += ', \n';
    }
    result += `${getTextVariant(currentLanguage, room.longName, room.longNameFr)}  (${room.code})`;
  });
  return result;
};

const handleSaveExpiry = (date) => {
  hotelWorkSheet.value.expires = date;
  editExpiryDate.value.isEdit = false;
};

const handleSaveDateBooked = (date) => {
  hotelWorkSheet.value.bookedDate = date;
  editDateBooked.value.isEdit = false;
};

const handleSaveHotelROH = () => {
  loadingWorksheetHotel.value = true;
  adminQuoteStore
    .getQuoteDetail(hotelWorkSheet.value?.sk)
    .then(() => {
      worksheetHotelPrices.value = [];
      handleFormatData();
    })
    .finally(() => {
      toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
      editHotelRoomCategory.value.index = null;
      loadingWorksheetHotel.value = false;
    });
};

const handleCancleHotelROH = (listRoom: string) => {
  editHotelRoomCategory.value.index = '';
  editHotelRoomCategory.value.data = listRoom;
};

const getDataIntoTable = (indexRoom: number) => {
  return worksheetHotelPrices.value[indexRoom];
};

const getChildPrices = (data: any) => {
  oldChildValue.value = cloneDeep(data.childPrices);
};

const onRowEditCancel = (event: DataTableCellEditCancelEvent, listValue: any) => {
  for (let i = 0; i < listValue[event.index].childPrices.length; i++) {
    listValue[event.index].childPrices[i] = oldChildValue.value[i];
  }
};

const initData = (num = 0) => {
  formatHotelData.depDate = hotelWorkSheet.value?.hotels?.[num]?.depDate;
  formatHotelData.duration = hotelWorkSheet.value?.hotels?.[num]?.duration;
  currentOption.value = num;
};

const renderStartIndex = (hotelWorkSheet: any, indexOption: number, indexHotel?: number) => {
  let finalIndex = 0;

  for (let i = 0; i < indexOption; i++) {
    const hotels = hotelWorkSheet[i]?.hotels || [];
    for (const hotel of hotels) {
      finalIndex += hotel.rooms?.length || 0;
    }
  }

  if (indexHotel) {
    const currentHotels = hotelWorkSheet[indexOption]?.hotels || [];
    for (let j = 0; j < indexHotel; j++) {
      finalIndex += currentHotels[j]?.rooms?.length || 0;
    }
  }
  return finalIndex;
};

const handleFormatData = () => {
  hotelWorkSheet.value?.hotels?.forEach((option: any, indexOption: number) => {
    editingRows.value[indexOption] = {};
    option.hotels.forEach((hotel: any, indexHotel: number) => {
      editingRows.value[indexOption][indexHotel] = [];
      hotel.rooms.forEach((room: any, indexRoom: number) => {
        const listPrice: any = filterPriceList(room.price, authorizations?.worksheet_hotel);
        if (indexRoom === 0 && indexHotel === 0) {
          doublePricePublic.value.push(listPrice[9].doublePrice);
        }
        numberDynamicColumns.value.push(listPrice?.[0]?.childPrices ? listPrice?.[0]?.childPrices?.length : listPrice?.[3]?.childPrices?.length);
        worksheetHotelPrices.value.push(listPrice);
      });
    });
  });
  editExpiryDate.value.data = hotelWorkSheet.value?.expires && new Date(hotelWorkSheet.value?.expires);
  editDateBooked.value.data = hotelWorkSheet.value?.bookedDate && new Date(hotelWorkSheet.value?.bookedDate);
  initData();
};

const handleChildHotelInput = (value: any, field: string, indexColumn: number) => {
  isShowError.value = !handleChildInput(value, field, indexColumn, validationInputs);
};

const handleCancleChildInput = (editorCancelCallback: any) => {
  editorCancelCallback();
  isShowError.value = false;
};

const handleSaveChildInput = (editorSaveCallback: any, data: any) => {
  if (data.rowName !== 'occupanciesEdit') {
    isShowError.value = false;
    editorSaveCallback();
  } else {
    checkEditChildInput(editorSaveCallback, isShowError.value);
  }
};
onBeforeMount(() => {
  handleFormatData();
});
</script>
<template>
  <LoadingSpinner v-if="loadingWorksheetHotel" />
  <div class="hotel-worksheet">
    <section id="header" class="my-3 px-3">
      <div class="grid">
        <div class="col-6 text-lg font-bold">{{ $t('worksheet.groups_quote') }}</div>
        <div class="col-6 text-right text-lg font-bold">({{ $t('worksheet.hotel_manual_quote') }})</div>
      </div>
    </section>
    <section id="information" class="px-3">
      <div class="grid">
        <div class="col-3">
          <div class="quote-id font-bold">
            <div class="grid">
              <div class="col-6 mb-4">{{ $t('worksheet.quote_id') }}:</div>
              <div class="col-6 font-normal">{{ hotelWorkSheet?.sk }}</div>
            </div>
          </div>
          <LinkedQuotesEditor :worksheetData="hotelWorkSheet" :permission="hasUserPermission(hotelWorkSheet, canEditHeader)" @changeLinkedQuote="changeLinkedQuoteHotel($event)"></LinkedQuotesEditor>
        </div>
        <div class="col-4">
          <div class="date-quote font-bold">
            <div class="grid">
              <div class="col-4 mb-4">{{ $t('worksheet.date_quoted') }}:</div>
              <div class="col-8 font-normal">{{ formatDate(hotelWorkSheet?.created, 'dd-LLL-yyyy', true, currentLanguage) }}</div>
            </div>
          </div>
          <div class="expiry-date font-bold">
            <div class="grid">
              <div class="col-4 mb-4">{{ $t('worksheet.expiry_date') }}:</div>
              <div class="col-8 font-normal">
                <div v-if="!editExpiryDate.isEdit" class="flex align-items-top">
                  <div class="min-w-5rem">
                    {{ formatDate(hotelWorkSheet?.expires, 'dd-LLL-yyyy', false, currentLanguage) }}
                  </div>
                  <span class="text-center ml-4">
                    <i v-if="hasUserPermission(hotelWorkSheet, canEditHeader)" class="pi pi-pencil cursor-pointer" @click="editExpiryDate.isEdit = true"></i>
                  </span>
                </div>
                <div v-else class="flex align-items-top">
                  <div class="w-9rem">
                    <DatePicker id="calendar-24h" v-model="editExpiryDate.data" dateFormat="dd-M-yy">
                      <template #footer>
                        <p class="text-center">
                          <small>{{ $t('common.press_space') }}</small>
                        </p>
                      </template>
                    </DatePicker>
                  </div>
                  <div class="text-center ml-4 flex gap-3 align-items-center">
                    <i class="pi pi-check cursor-pointer" v-if="editExpiryDate.data" @click="handleSaveExpiry(editExpiryDate.data)"></i>
                    <i class="pi pi-times cursor-pointer" @click="editExpiryDate = { isEdit: false, data: hotelWorkSheet?.expires && new Date(hotelWorkSheet?.expires) }"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="book-date font-bold">
            <div class="grid">
              <div class="col-4">{{ $t('worksheet.book_dated') }}:</div>
              <div class="col-8 font-normal">
                <div v-if="!editDateBooked.isEdit" class="flex align-items-top">
                  <div class="min-w-5rem">
                    {{ formatDate(hotelWorkSheet?.bookedDate, 'dd-LLL-yyyy', false, currentLanguage) }}
                  </div>
                  <span class="text-center ml-4">
                    <i v-if="hasUserPermission(hotelWorkSheet, canEditHeader)" class="pi pi-pencil cursor-pointer" @click="editDateBooked.isEdit = true"></i>
                  </span>
                </div>
                <div v-else class="flex align-items-top">
                  <div class="w-9rem">
                    <DatePicker id="calendar-24h" v-model="editDateBooked.data" dateFormat="dd-M-yy">
                      <template #footer>
                        <p class="text-center">
                          <small>{{ $t('common.press_space') }}</small>
                        </p>
                      </template>
                    </DatePicker>
                  </div>
                  <div class="text-center flex ml-4 flex gap-3 align-items-center">
                    <i class="pi pi-check cursor-pointer" @click="handleSaveDateBooked(editDateBooked.data)"></i>
                    <i class="pi pi-times cursor-pointer" @click="editDateBooked = { isEdit: false, data: hotelWorkSheet?.bookedDate && new Date(hotelWorkSheet?.bookedDate) }"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-5">
          <div class="quote-assigned-to font-bold">
            <div class="grid">
              <div class="col-4 mb-4">{{ $t('worksheet.quote_assigned_to') }}:</div>
              <div class="col-8 font-normal">{{ hotelWorkSheet?.assignedTo === UNASSIGNED ? $t('status.unassigned') : hotelWorkSheet?.assignedTo }}</div>
            </div>
          </div>

          <div class="book-by font-bold">
            <div class="grid">
              <div class="col-4">{{ $t('worksheet.book_by') }}:</div>
              <div class="col-8 font-normal flex gap-1">
                <div>{{ hotelWorkSheet?.user }}</div>
                <div>{{ hotelWorkSheet?.email && `(${hotelWorkSheet?.email})` }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    <section id="details">
      <Tabs :value="0" @update:value="initData($event)">
        <TabList>
          <Tab v-for="(_, index) in hotelWorkSheet?.hotels" :key="index" :value="index">{{ $t('common.option') + ' ' + Number(index + 1) }}</Tab>
          <Tab v-if="hasUserPermission(hotelWorkSheet, canViewTermConditions)" :key="hotelWorkSheet?.hotels.length" :value="hotelWorkSheet?.hotels.length">{{ $t('menu.term_and_condition') }}</Tab>
        </TabList>
        <TabPanels>
          <TabPanel v-for="(option, indexOption) in hotelWorkSheet?.hotels" :key="indexOption" :value="indexOption">
            <div class="average-net-margin">
              <b>{{ `${$t('worksheet.average_net_margin')}:` }}</b>
              <span>{{ doublePricePublic[indexOption] }}</span>
            </div>
            <div>
              <HotelInform
                :optionData="option"
                :permission="canEditHeader"
                :hotelWorkSheet="hotelWorkSheet"
                v-model:editAdult="editAdult"
                v-model:editChild="editChild"
                v-model:editNumberOfRoom="editNumberOfRoom"
                @handleSaveAdult="(adult) => handleSaveAdult(adult, option)"
                @handleSaveChild="(child) => handleSaveChild(child, option)"
                @handleSaveNumberOfRoom="(numOfRoom) => handleSaveNumberOfRoom(numOfRoom, option)"
              />
            </div>
            <div class="hotel-inform-detail">
              <div v-for="(hotel, indexHotel) in option?.hotels" :key="indexHotel">
                <div class="option">
                  <div class="lead-option">{{ $t('worksheet.hotel') }}</div>
                  <div class="detail-option flex justify-content-between flex-wrap lg:flex-nowrap">
                    <div>
                      <div class="destination font-bold mt-2 mb-2">
                        <div class="flex gap-3">
                          <div class="min-w-label">{{ $t('common.destination') }}:</div>
                          <div class="font-normal">{{ option.airportCode }} ({{ option.dest }})</div>
                        </div>
                      </div>
                      <div class="hotel font-bold mb-2">
                        <div class="flex gap-3">
                          <div class="min-w-label">{{ $t('worksheet.hotel') }}:</div>
                          <div class="font-normal">{{ hotel.hotel }} ({{ hotel.hCode }})</div>
                        </div>
                      </div>
                      <div class="hotel-room-cat font-bold mb-2">
                        <div class="flex gap-3" v-if="editHotelRoomCategory.index !== `hotel${indexOption}${indexHotel}`">
                          <div class="min-w-label">{{ $t('worksheet.hotel_room_category') }}:</div>
                          <div class="font-normal white-space-pre-line">{{ getHotelRoom(hotel) }}</div>
                          <div class="text-center" v-if="canEditRoh && isIncentiveGroupType">
                            <i class="pi pi-pencil cursor-pointer" @click="editHotelRoomCategory = { index: `hotel${indexOption}${indexHotel}`, data: getHotelRoom(hotel) }"></i>
                          </div>
                        </div>
                        <div v-else>
                          <RunOfHouseSelection
                            :quoteId="hotelWorkSheet?.sk"
                            :hotelIndex="indexHotel"
                            :optionIndex="indexOption"
                            :defaultValue="getHotelRoom(hotel)"
                            @saveCategory="handleSaveHotelROH()"
                            @cancelEditCategory="handleCancleHotelROH(getHotelRoom(hotel))"
                          ></RunOfHouseSelection>
                        </div>
                      </div>
                    </div>
                    <div class="mt-2">
                      <div class="flex justify-content-end flex-column align-items-end gap-2">
                        <Button v-if="canAdjustOptionMargin" data-testid="adjust-option-margin" class="w-full flex justify-content-center h-3rem uppercase font-medium action-button" @click="handleOpenAdjustModal(hotel, indexOption, indexHotel)">{{
                          $t('worksheet.adjust_option_margin')
                        }}</Button>
                        <Button
                          v-if="authorizations.quote_management.cost_calculation_view"
                          data-testid="cost-calculation-tool"
                          :loading="isLoadingTool"
                          class="w-full flex justify-content-center h-3rem uppercase font-medium action-button"
                          @click="handleOpenCostCalculationModal(hotel, indexOption, indexHotel)"
                          >{{ $t('worksheet.cost_calculation_tool') }}</Button
                        >
                      </div>
                    </div>
                  </div>
                </div>

                <div v-for="(hot, roomIndex) in hotel.rooms" :key="roomIndex">
                  <div class="pricing">
                    <div class="lead-pricing flex justify-content-between align-items-center">
                      {{ $t('common.pricing') }} ({{ getTextVariant(currentLanguage, hot.longName, hot.longNameFr) }} - {{ hot.code }})
                      <Button size="small" severity="danger" :class="roomIndex == 0 ? 'custom-button' : 'hidden'" :label="$t('common.buttons.process')" @click="handleOpenModal(hotelWorkSheet, indexOption, indexHotel, 'hotels')" />
                    </div>
                    <DataTable
                      v-model:editingRows="editingRows[indexOption][indexHotel][roomIndex]"
                      :value="getDataIntoTable(renderStartIndex(hotelWorkSheet?.hotels, indexOption, indexHotel) + roomIndex)"
                      class="w-full"
                      editMode="row"
                      data-key="rowName"
                      showGridlines
                      @row-edit-save="onRowEditSave($event, hotelWorkSheet, indexOption, indexHotel, roomIndex)"
                      @row-edit-cancel="onRowEditCancel($event, getDataIntoTable(renderStartIndex(hotelWorkSheet?.hotels, indexOption, indexHotel) + roomIndex))"
                    >
                      <TableColumn header="" dataType="string" class="relative">
                        <template #body="{ data }">
                          <div :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'text-900 font-bold']">
                            <span>{{ convertOccNameTable(data.rowName) }}</span>
                          </div>
                        </template>
                      </TableColumn>
                      <TableColumn field="singlePrice" :header="$t('book_form.single')" dataType="number" class="relative">
                        <template #body="{ data, field, index }">
                          <div v-if="index" :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'bg-white price-header']">
                            <span :class="data.key === PRICE_KEY.HEADER && 'opacity-0'">{{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}</span>
                          </div>
                        </template>
                        <template #editor="{ data, field, index }">
                          <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" class="w-full" />
                        </template>
                      </TableColumn>
                      <TableColumn field="doublePrice" :header="$t('book_form.double')" dataType="number" class="relative">
                        <template #body="{ data, field, index }">
                          <div v-if="index" :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'bg-white price-header']">
                            <span :class="data.key === PRICE_KEY.HEADER && 'opacity-0'">{{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}</span>
                          </div>
                        </template>
                        <template #editor="{ data, field, index }">
                          <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" class="w-full" />
                        </template>
                      </TableColumn>
                      <TableColumn field="triplePrice" :header="$t('book_form.triple')" dataType="number" class="relative">
                        <template #body="{ data, field, index }">
                          <div v-if="index" :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'bg-white price-header']">
                            <span :class="data.key === PRICE_KEY.HEADER && 'opacity-0'">{{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}</span>
                          </div>
                        </template>
                        <template #editor="{ data, field, index }">
                          <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" class="w-full" />
                        </template>
                      </TableColumn>
                      <TableColumn field="quadPrice" :header="$t('book_form.quad')" dataType="number" class="relative">
                        <template #body="{ data, field, index }">
                          <div v-if="index" :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'bg-white price-header']">
                            <span :class="data.key === PRICE_KEY.HEADER && 'opacity-0'">{{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}</span>
                          </div>
                        </template>
                        <template #editor="{ data, field, index }">
                          <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" class="w-full" />
                        </template>
                      </TableColumn>
                      <TableColumn field="fiveInRoomPrice" :header="$t('book_form.5_in_room')" dataType="number" class="relative">
                        <template #body="{ data, field, index }">
                          <div v-if="index" :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'bg-white price-header']">
                            <span :class="data.key === PRICE_KEY.HEADER && 'opacity-0'">{{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}</span>
                          </div>
                        </template>
                        <template #editor="{ data, field, index }">
                          <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" class="w-full" />
                        </template>
                      </TableColumn>
                      <TableColumn field="sixInRoomPrice" :header="$t('book_form.6_in_room')" dataType="number" class="relative">
                        <template #body="{ data, field, index }">
                          <div v-if="index" :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'bg-white price-header']">
                            <span :class="data.key === PRICE_KEY.HEADER && 'opacity-0'">{{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}</span>
                          </div>
                        </template>
                        <template #editor="{ data, field, index }">
                          <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" class="w-full" />
                        </template>
                      </TableColumn>
                      <TableColumn
                        field="childPrices"
                        :header="$t('common.child')"
                        class="min-w-8rem relative parent-column"
                        v-for="(column, indexColumn) in numberDynamicColumns[renderStartIndex(hotelWorkSheet?.hotels, indexOption, indexHotel) + roomIndex]"
                        :key="'childColumn' + indexColumn"
                      >
                        <template #body="{ data, field }">
                          <div :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'bg-white price-header ']">
                            <span :class="data.key === PRICE_KEY.HEADER && 'opacity-0'">
                              {{ data.rowName && data.rowName === 'occupanciesEdit' ? data[field]?.[indexColumn] : Number(data[field]?.[indexColumn]) ? Number(data[field]?.[indexColumn]).toFixed(2) : '0.00' }}
                            </span>
                          </div>
                        </template>
                        <template #editor="{ data, field, index }">
                          <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field][indexColumn]" class="w-full" />
                          <InputText v-else v-model="data[field][indexColumn]" @input="handleChildHotelInput(data[field][indexColumn], field, indexColumn)" class="w-full" />
                        </template>
                      </TableColumn>
                      <TableColumn field="infantPrice" :header="$t('common.infant')" dataType="number" class="relative">
                        <template #body="{ data, field, index }">
                          <div v-if="index === 0">
                            {{ data[field] }}
                          </div>
                          <div v-else :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'bg-white price-header']">
                            <span :class="data.key === PRICE_KEY.HEADER && 'opacity-0'">{{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}</span>
                          </div>
                        </template>
                        <template #editor="{ data, field, index }">
                          <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" class="w-full" />
                          <InputText v-else v-model="data[field]" class="w-full" />
                        </template>
                      </TableColumn>
                      <TableColumn :rowEditor="true" class="text-center relative w-5rem" :header="$t('common.action')">
                        <template #body="{ editorInitCallback, data }">
                          <div
                            :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium edit-class', data.key === PRICE_KEY.HEADER && 'bg-white price-header right-0']"
                            @click="getChildPrices(data)"
                          >
                            <i v-if="data.key !== TOTAL_COST && data.key !== PRICE_KEY.HEADER && data.canEdit" class="pi pi-pencil" @click="editorInitCallback"></i>
                          </div>
                          <p v-if="data.key === TOTAL_COST || data.key === PRICE_KEY.HEADER" class="opacity-0">{{ $t('common.edit') }}</p>
                        </template>
                        <template #editor="{ data, editorSaveCallback, editorCancelCallback }">
                          <div class="flex">
                            <i class="pi pi-check edit-pi" @click="handleSaveChildInput(editorSaveCallback, data)"></i>
                            <i class="pi pi-times edit-pi" @click="handleCancleChildInput(editorCancelCallback)"></i>
                          </div>
                        </template>
                      </TableColumn>
                    </DataTable>
                  </div>
                </div>
                <div class="promotions" v-if="hasUserPermission(hotelWorkSheet, canViewPromotion)">
                  <div class="lead-promotions flex justify-content-between align-items-center">
                    {{ $t('worksheet.promotions') }} <Button size="small" class="process-button" severity="danger" :label="$t('common.buttons.process')" @click="handleOpenModal(hotelWorkSheet, indexOption, indexHotel, 'hotels', true)" />
                  </div>
                  <div v-if="promoEditedData.index !== `hotel${indexOption}${indexHotel}`" class="flex">
                    <div class="w-full">
                      <HTMLEditor :readonly="true" v-model="hotel.promotion" class="custom-editor" />
                    </div>
                    <span class="text-center">
                      <i v-if="hasUserPermission(hotelWorkSheet, canEditPromotion)" class="pi pi-pencil edit-pi cursor-pointer" @click="promoEditedData = { index: `hotel${indexOption}${indexHotel}`, data: hotel.promotion }"></i>
                    </span>
                  </div>
                  <div v-else class="flex">
                    <div class="w-full">
                      <HTMLEditor v-model="promoEditedData.data" class="custom-editor" />
                    </div>
                    <div class="text-center flex flex-col">
                      <i class="pi pi-check edit-pi cursor-pointer" @click="handleSavePromo(hotel)"></i>
                      <i class="pi pi-times edit-pi cursor-pointer" @click="promoEditedData.index = null"></i>
                    </div>
                  </div>
                </div>
                <div class="additional-hotel">
                  <div class="lead-additional-hotel">{{ $t('worksheet.additional_hotel_detail') }}</div>
                  <div v-if="editAdditionalHotel.index !== `hotel${indexOption}${indexHotel}`" class="flex">
                    <div class="w-full">
                      <HTMLEditor :readonly="true" v-model="hotel.additionalHotelDetail" class="custom-editor" />
                    </div>
                    <span class="text-center">
                      <i class="pi pi-pencil edit-pi cursor-pointer" @click="editAdditionalHotel = { index: `hotel${indexOption}${indexHotel}`, data: hotel.additionalHotelDetail }"></i>
                    </span>
                  </div>
                  <div v-else class="flex">
                    <div class="w-full">
                      <HTMLEditor v-model="editAdditionalHotel.data" class="custom-editor" />
                    </div>
                    <div class="text-center flex flex-col">
                      <i class="pi pi-check edit-pi cursor-pointer" @click="handleSaveAdditionalHotel(hotel)"></i>
                      <i class="pi pi-times edit-pi cursor-pointer" @click="editAdditionalHotel.index = null"></i>
                    </div>
                  </div>
                </div>
                <div class="agency-comments" v-if="hasUserPermission(hotelWorkSheet, canViewAgencyComment)">
                  <div class="lead-comments">{{ $t('worksheet.agency_comment') }}</div>
                  <div v-if="editComment.index !== `hotel${indexOption}${indexHotel}`" class="flex">
                    <div class="w-full">
                      <HTMLEditor :readonly="true" v-model="hotel.comment" class="custom-editor" />
                    </div>
                    <span class="text-center">
                      <i v-if="hasUserPermission(hotelWorkSheet, canEditAgencyComment)" class="pi pi-pencil edit-pi cursor-pointer" @click="editComment = { index: `hotel${indexOption}${indexHotel}`, data: hotel.comment }"></i>
                    </span>
                  </div>
                  <div v-else class="flex">
                    <div class="w-full">
                      <HTMLEditor v-model="editComment.data" class="custom-editor" />
                    </div>
                    <div class="flex flex-col text-center">
                      <i class="pi pi-check edit-pi cursor-pointer" @click="handleSaveComment(hotel)"></i>
                      <i class="pi pi-times edit-pi cursor-pointer" @click="editComment.index = null"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="hotel-prefer mt-6" v-for="(hotelPrefer, indexHotelPrefer) in option.hotelPrefer" :key="indexHotelPrefer">
              <div class="title-hotel-prefer">
                <div class="title-hotel font-bold">{{ $t('worksheet.title_hotel_prefer') + ' - ' + hotelPrefer.hotel }}</div>
              </div>
              <div class="hotel-detail mt-3">
                <div class="title-hotel font-bold mb-3 text-900">{{ $t('worksheet.hotel_detail') }}</div>
                <div class="flex gap-6 flex-wrap md:flex-nowrap">
                  <div>
                    <img :src="option.hotelPreferPdf[indexHotelPrefer]?.imgUrl || fallBackImage" alt="" class="h-15rem w-22rem" />
                  </div>
                  <div class="min-w-20rem">
                    <div class="mb-1 font-bold text-lg text-900">{{ option.hotelPreferPdf[indexHotelPrefer]?.hotel }}</div>
                    <div class="mb-1">
                      <i v-for="(item, index) in parseInt(option.hotelPreferPdf[indexHotelPrefer]?.overallRate / 2)" :key="index" class="pi mr-1 pi-star-fill text-2xl text-color-secondary"></i>
                      <i v-if="+option.hotelPreferPdf[indexHotelPrefer]?.overallRate % 2 > 0 || true" class="text-color-secondary relative top-2px">
                        <img :src="semistarImage" alt="star" />
                      </i>
                    </div>
                    <p class="text-sm text-900">{{ option.hotelPreferPdf[indexHotelPrefer]?.description }}</p>
                    <div class="text-900 text-sm">
                      <a href="javascript:" class="text-lg" @click="handleRedirectLink($event, option.hotelPreferPdf[indexHotelPrefer]?.urlDetail)">{{ $t('worksheet.hotel_detail') }}</a>
                      ({{ $t('worksheet.hint') }})
                    </div>
                  </div>
                </div>
              </div>
              <div class="promotions" v-if="hasUserPermission(hotelWorkSheet, canViewPromotion)">
                <div class="lead-promotions flex justify-content-between align-items-center">
                  {{ $t('worksheet.promotions') }}
                  <Button size="small" severity="danger" class="process-button" :label="$t('common.buttons.process')" @click="handleOpenModal(hotelWorkSheet, indexOption, indexHotelPrefer, 'hotelPrefer', true)" />
                </div>
                <div v-if="promoEditedData.index !== `preferHotel${indexHotelPrefer}`" class="flex">
                  <div class="w-full">
                    <HTMLEditor :readonly="true" v-model="hotelPrefer.promotion" class="custom-editor" />
                  </div>
                  <span class="text-center">
                    <i v-if="hasUserPermission(hotelWorkSheet, canEditPromotion)" class="pi pi-pencil edit-pi cursor-pointer" @click="promoEditedData = { index: `preferHotel${indexHotelPrefer}`, data: hotelPrefer.promotion }"></i>
                  </span>
                </div>
                <div v-else class="flex">
                  <div class="w-full">
                    <HTMLEditor v-model="promoEditedData.data" class="custom-editor" />
                  </div>
                  <div class="flex flex-col text-center">
                    <i class="pi pi-check edit-pi cursor-pointer" @click="handleSavePromo(hotelPrefer)"></i>
                    <i class="pi pi-times edit-pi cursor-pointer" @click="promoEditedData.index = null"></i>
                  </div>
                </div>
              </div>
              <div class="agency-comments" v-if="hasUserPermission(hotelWorkSheet, canViewAgencyComment)">
                <div class="lead-comments">{{ $t('worksheet.agency_comment') }}</div>
                <div v-if="editComment.index !== `preferHotel${indexHotelPrefer}`" class="flex">
                  <div class="w-full">
                    <HTMLEditor :readonly="true" v-model="hotelPrefer.comment" class="custom-editor" />
                  </div>
                  <span class="text-center">
                    <i v-if="hasUserPermission(hotelWorkSheet, canEditAgencyComment)" class="pi pi-pencil edit-pi cursor-pointer" @click="editComment = { index: `preferHotel${indexHotelPrefer}`, data: hotelPrefer.comment }"></i>
                  </span>
                </div>
                <div v-else class="flex">
                  <div class="w-full">
                    <HTMLEditor v-model="editComment.data" class="custom-editor" />
                  </div>
                  <div class="flex flex-col text-center">
                    <i class="pi pi-check edit-pi cursor-pointer" @click="handleSaveComment(hotelPrefer)"></i>
                    <i class="pi pi-times edit-pi cursor-pointer" @click="editComment.index = null"></i>
                  </div>
                </div>
              </div>
            </div>
          </TabPanel>
          <TabPanel :value="hotelWorkSheet?.hotels.length">
            <div class="term_of_c">
              <div class="intro">
                <h4 class="font-bold text-lg">{{ $t('common.intro') }}</h4>
                <div v-if="!editIntro.isEdit" class="flex">
                  <div class="w-full">
                    <HTMLEditor :readonly="true" v-model="hotelWorkSheet.intro" class="custom-editor" />
                  </div>
                  <span class="text-center">
                    <i v-if="hasUserPermission(hotelWorkSheet, canEditTermConditions)" class="pi pi-pencil edit-pi cursor-pointer" @click="editIntro = { isEdit: true, data: hotelWorkSheet.intro }"></i>
                  </span>
                </div>
                <div v-else class="flex">
                  <div class="w-full">
                    <HTMLEditor v-model="editIntro.data" class="custom-editor" />
                  </div>
                  <div class="text-center flex flex-col">
                    <i class="pi pi-check edit-pi cursor-pointer" @click="handleSaveIntro(hotelWorkSheet)"></i>
                    <i class="pi pi-times edit-pi cursor-pointer" @click="editIntro.isEdit = false"></i>
                  </div>
                </div>
              </div>
              <div class="term_conditions mt-4">
                <h4 class="font-bold text-lg">{{ $t('menu.term_and_condition') }}</h4>
                <div v-if="!editTermConditions.isEdit" class="flex">
                  <div class="w-full">
                    <HTMLEditor :readonly="true" v-model="hotelWorkSheet.termConditions" class="custom-editor" />
                  </div>
                  <span class="text-center">
                    <i v-if="hasUserPermission(hotelWorkSheet, canEditTermConditions)" class="pi pi-pencil edit-pi cursor-pointer" @click="editTermConditions = { isEdit: true, data: hotelWorkSheet.termConditions }"></i>
                  </span>
                </div>
                <div v-else class="flex">
                  <div class="w-full">
                    <HTMLEditor v-model="editTermConditions.data" class="custom-editor" />
                  </div>
                  <div class="text-center flex flex-col">
                    <i class="pi pi-check edit-pi cursor-pointer" @click="handleSaveTermCondition(hotelWorkSheet)"></i>
                    <i class="pi pi-times edit-pi cursor-pointer" @click="editTermConditions.isEdit = false"></i>
                  </div>
                </div>
              </div>
            </div>
          </TabPanel>
        </TabPanels>
      </Tabs>
      <div class="flex justify-content-center mt-2 gap-3" v-if="hasUserPermission(hotelWorkSheet, false)">
        <Button id="hotel-save-btn" :label="$t('promos.save')" icon="pi pi-check" class="p-button action-button" @click="onSave(hotelWorkSheet)" />
        <ViewPDFFile :quoteID="hotelWorkSheet.sk" :disabled="disablePreviewPDF" isShowBtn></ViewPDFFile>
      </div>
      <Dialog v-model:visible="visible" header=" " modal>
        <div v-if="isLoading" class="flex align-items-center flex-col modal progress-worksheet-modal">
          <h2 class="font-bold text-2xl">{{ $t('worksheet.hotel_call_progress') }}</h2>
          <p v-if="isPromotionModal">{{ $t('worksheet.retrieve_group') }}</p>
          <p v-else>{{ $t('worksheet.retrieve_tts') }}</p>
          <div class="w-3rem h-3rem rotage"><img class="w-full" :alt="$t('common.generic.loading')" :src="loadingIcon" /></div>
        </div>
        <div v-else class="flex align-items-left flex-col modal">
          <p class="font-bold text-2xl text-center">{{ $t('worksheet.hotel_call_completed') }}</p>
          <div v-if="!isPromotionModal" class="text-lg mb-3">
            <div v-if="!newPrice">
              <i class="pi pi-times-circle text-red-700 font-bold text-xl mr-1"></i>
              <span>{{ $t('common.error') }} - {{ errorMessage ? errorMessage : $t('worksheet.un_retrieve_tts') }}</span>
            </div>
            <div v-else>
              <i class="pi pi-check-circle text-green-700 font-bold text-xl mr-1"></i>
              <span>{{ $t('worksheet.succ_retrieve_tts') }}</span>
            </div>
          </div>
          <div v-else class="text-lg mb-3">
            <div v-if="!newHcms">
              <i class="pi pi-times-circle text-red-700 font-bold text-xl mr-1"></i>
              <span>{{ $t('worksheet.un_retrieve_hcms') }}</span>
            </div>
            <div v-else>
              <i class="pi pi-check-circle text-green-700 font-bold text-xl mr-1"></i>
              <span v-if="isEmptyRespond">{{ $t('worksheet.empty_retrieve_hcms') }}</span>
              <span v-else>{{ $t('worksheet.succ_retrieve_hcms') }}</span>
            </div>
          </div>
          <div :class="['flex justify-content-between', !isPromotionModal && 'm-auto']">
            <Button v-if="!isPromotionModal" icon="pi pi-eye" :label="$t('common.buttons.view_pricing')" @click="previewNewPriceHotel(newDataPricing, hotelWorkSheet)" />
            <Button v-else icon="pi pi-eye" :label="$t('common.buttons.view')" @click="previewNewPromotionHotel(newDataPricing)" />
            <Button v-if="isPromotionModal" icon="pi pi-check" :label="$t('common.buttons.close_apply')" @click="handleCloseAndApplyPromotion(hotelWorkSheet, newDataPricing)" />
          </div>
        </div>
      </Dialog>
    </section>
  </div>
</template>
<style lang="scss" scoped>
:deep() {
  .p-inputtextarea:disabled {
    opacity: 1;
    cursor: not-allowed;
  }
}

:deep(.p-inputnumber .p-inputnumber-input) {
  width: 100%;
}
</style>
